import { IconFile, IconLock, IconTrash } from '@shared/ui/icons';
import { Select, Label } from '@shared/ui/Input';
import type { EconModuleConfig } from '@entities/economic/types';
import {
  DOC_ICONS,
  DupNote,
  FileItem,
  FileName,
  FileRow,
  FileSize,
  PiiNote,
  RemoveBtn,
  TypeHint,
  TypeIconBox,
  TypeRow,
  UnsetNote,
} from './styles';

/** Один приложенный документ: файл + выбранный пользователем тип. `id` —
 *  стабильный ключ для React (имя файла не уникально — можно приложить
 *  два «Реестр.xlsx» как разные типы). */
export interface AttachedDoc {
  id: string;
  file: File;
  docType: string;
}

/** Человекочитаемый размер файла: 24 КБ / 1.4 МБ. */
export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`;
}

let docSeq = 0;
export function nextDocId(): string {
  docSeq += 1;
  return `doc-${docSeq}`;
}

/** Sentinel «тип ещё не выбран». Backend такого поля не примет — submit с ним
 *  заблокирован. */
export const UNSET_TYPE = '';

export function FileItemView({
  doc,
  cfg,
  isDuplicate,
  onRemove,
  onChangeType,
}: {
  doc: AttachedDoc;
  cfg: EconModuleConfig;
  isDuplicate: boolean;
  onRemove: (id: string) => void;
  onChangeType: (id: string, type: string) => void;
}) {
  const dt = cfg.docTypes.find((t) => t.docType === doc.docType);
  const selectId = `econ-doctype-${doc.id}`;
  return (
    <FileItem>
      <FileRow>
        <FileName title={doc.file.name}>
          <IconFile size={15} />
          {doc.file.name}
        </FileName>
        <FileSize>{fmtSize(doc.file.size)}</FileSize>
        <RemoveBtn
          type="button"
          onClick={() => onRemove(doc.id)}
          aria-label={`Убрать файл ${doc.file.name}`}
          title="Убрать файл"
        >
          <IconTrash size={16} />
        </RemoveBtn>
      </FileRow>
      <Label htmlFor={selectId}>Тип документа</Label>
      <TypeRow>
        <TypeIconBox aria-hidden="true">
          {dt ? (
            (() => {
              const TypeIcon = DOC_ICONS[dt.icon];
              return <TypeIcon size={18} />;
            })()
          ) : (
            <IconFile size={18} />
          )}
        </TypeIconBox>
        <Select
          id={selectId}
          value={doc.docType}
          onChange={(e) => onChangeType(doc.id, e.target.value)}
          aria-invalid={!doc.docType}
        >
          <option value="" disabled>
            — выберите тип —
          </option>
          {cfg.docTypes.map((t) => (
            <option key={t.docType} value={t.docType}>
              {t.label}
            </option>
          ))}
        </Select>
      </TypeRow>
      {dt ? (
        <TypeHint>
          {dt.hint}
          {dt.pii && (
            <PiiNote>
              <IconLock size={12} /> Содержит ФИО — обезличивается автоматически.
            </PiiNote>
          )}
        </TypeHint>
      ) : (
        <UnsetNote>
          Тип не определён по имени файла — выберите его вручную.
        </UnsetNote>
      )}
      {isDuplicate && (
        <DupNote>
          Несколько файлов с одинаковым типом «{dt?.label}». Для этого типа
          ожидается один файл — проверьте выбор.
        </DupNote>
      )}
    </FileItem>
  );
}
